/**
 * Utilities for extracting GA4 data from context (query params, cookies, etc.)
 * Similar to GA4EventDispatcher.#extractGa4Data from Bifrost
 */
import { isAllowedParentLocation } from './allowed-origin'

export interface GA4Data {
  clientId?: string
  pageLocation?: string
  pageReferrer?: string
  sessionId?: string
}

/**
 * Resolves the browsing context whose URL/referrer should attribute GA4 events.
 *
 * When the auth app is loaded inside the Bifrost iframe (e.g. /login?usage=iframe
 * embedded on any Jusbrasil page), `window.location.href` is the *iframe's* URL,
 * not the page the user is actually on. Bifrost's frontend solved this by using
 * `top.location.href` / `top.document.referrer` so that login/sign_up GA4 events
 * carry the parent page as `page_location` — preserving attribution and content
 * grouping. This restores parity.
 *
 * Falls back to the current window when:
 * - Not in an iframe (`window.top === window`).
 * - Parent origin is not allow-listed (cross-origin access would throw anyway).
 * - Any access to `window.top` throws (defensive — DOMException SecurityError).
 */
function getAttributionContext(): { href?: string; referrer?: string } {
  if (typeof window === 'undefined') return {}

  const topWindow = window.top
  const isEmbedded = topWindow !== null && topWindow !== window
  if (!isEmbedded || !isAllowedParentLocation()) {
    return { href: window.location.href, referrer: document.referrer }
  }

  try {
    return {
      href: topWindow.location.href,
      referrer: topWindow.document.referrer,
    }
  } catch {
    return { href: window.location.href, referrer: document.referrer }
  }
}

/**
 * Extracts GA4 data from URL query parameters
 * The Bifrost sends ga4_data as a JSON string in query params or body
 */
export function extractGA4DataFromQuery(): GA4Data | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const urlParams = new URLSearchParams(window.location.search)
    const ga4DataParam = urlParams.get('ga4_data')

    if (!ga4DataParam) {
      return null
    }

    const parsed = JSON.parse(ga4DataParam) as GA4Data
    return parsed
  } catch {
    return null
  }
}

/**
 * Extracts client ID from GA4 cookies
 * Similar to GA4EventDispatcher.#extractClientIdFromCookie from Bifrost
 */
function extractClientIdFromCookie(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const ga4ClientId = getCookie('ga4_client_id')
  if (ga4ClientId) {
    return ga4ClientId
  }

  const gaCookie = getCookie('_ga')
  if (gaCookie) {
    const versionValidate = /^(?:GA)?1\./
    if (versionValidate.test(gaCookie)) {
      return gaCookie.split('.').slice(-2).join('.')
    }
  }

  return null
}

/**
 * Extracts session ID from GA4 cookies
 * Similar to GA4EventDispatcher.#extractSessionIdFromCookie from Bifrost
 *
 * Tries to extract from:
 * 1. Custom ga4_session_id cookie (primary)
 * 2. Internal GA4 cookie (_ga_<MEASUREMENT_ID>) using NEXT_PUBLIC_GA4_MEASUREMENT_ID
 *
 * Note: Requires NEXT_PUBLIC_GA4_MEASUREMENT_ID to be configured (like Bifrost requires measurementId from config).
 */
function extractSessionIdFromCookie(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const ga4SessionId = getCookie('ga4_session_id')
  if (ga4SessionId) {
    return ga4SessionId
  }

  const measurementId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID
  if (measurementId) {
    const cookieName = measurementId.replace('G-', '_ga_')
    const cookie = getCookie(cookieName)
    if (cookie) {
      return cookie.split('.')[2] || null
    }
  }

  return null
}

/**
 * Helper to get cookie value by name
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const cookies = document.cookie.split(';')
  const cookie = cookies.find((c) => c.trim().startsWith(`${name}=`))
  if (cookie) {
    const value = cookie.split('=').slice(1).join('=')
    return decodeURIComponent(value)
  }

  return null
}

/**
 * Extracts GA4 data from all available sources (query params, cookies, window)
 * Returns combined GA4 data object
 */
export function extractGA4Data(): GA4Data {
  const queryData = extractGA4DataFromQuery()
  const clientId = queryData?.clientId || extractClientIdFromCookie()
  const sessionId = queryData?.sessionId || extractSessionIdFromCookie()
  const { href, referrer } = getAttributionContext()
  const pageLocation = queryData?.pageLocation || href
  const pageReferrer = queryData?.pageReferrer || referrer

  return {
    clientId: clientId || undefined,
    pageLocation,
    pageReferrer,
    sessionId: sessionId || undefined,
  }
}
